import React, { useEffect, useState } from 'react';
import { Avatar, Box, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Stack, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField, CircularProgress, Tabs, Tab, Chip, MenuItem, Divider, Grid, InputAdornment } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAllMembers, deleteMember as deleteMemberAction, fetchMembersForSearchTable, fetchAllUpdateRequests, fetchUpdateRequestById, approveUpdateRequest, rejectUpdateRequest } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import CreateMemberDialog from './CreateMemberDialog';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { getMemberUpdateRequestChanges } from './memberUpdateRequestUtils';

const MembersList = () => {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Update requests state
  const [updateRequests, setUpdateRequests] = useState([]);
  const [updateRequestsPage, setUpdateRequestsPage] = useState(1);
  const [updateRequestsPageSize] = useState(10);
  const [updateRequestsTotalPages, setUpdateRequestsTotalPages] = useState(1);
  const [updateRequestsLoading, setUpdateRequestsLoading] = useState(false);
  const [updateRequestsStatusFilter, setUpdateRequestsStatusFilter] = useState('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetail, setRequestDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const searchActive = searchQuery.trim().length >= 2;

  useEffect(() => {
    if (tab !== 0) return;

    if (searchActive) {
      let active = true;
      const timer = setTimeout(async () => {
        setLoading(true);
        try {
          const rows = await fetchMembersForSearchTable(searchQuery.trim());
          if (!active) return;
          setRows(rows);
          setTotalPages(1);
        } catch (e) {
          if (!active) return;
          setRows([]);
        } finally {
          if (active) setLoading(false);
        }
      }, 300);
      return () => {
        active = false;
        clearTimeout(timer);
      };
    }

    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data, meta } = await fetchAllMembers({ pageNumber: page, pageSize });
        if (!active) return;
        setRows(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [page, pageSize, tab, searchQuery, searchActive]);

  // Fetch update requests when tab 1 is selected
  useEffect(() => {
    if (tab !== 1) return;
    let active = true;
    (async () => {
      setUpdateRequestsLoading(true);
      try {
        const params = { pageNumber: updateRequestsPage, pageSize: updateRequestsPageSize };
        if (updateRequestsStatusFilter !== '') {
          params.status = Number(updateRequestsStatusFilter);
        }
        const { data, meta } = await fetchAllUpdateRequests(params);
        if (!active) return;
        setUpdateRequests(data || []);
        setUpdateRequestsTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setUpdateRequests([]);
      } finally {
        if (active) setUpdateRequestsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [tab, updateRequestsPage, updateRequestsPageSize, updateRequestsStatusFilter]);

  const [createOpen, setCreateOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const getStatusLabel = (status) => {
    switch (status) {
      case 0: return 'Beklemede';
      case 1: return 'Onaylandı';
      case 2: return 'Reddedildi';
      default: return 'Bilinmiyor';
    }
  };

  const getMembershipTypeLabel = (type) => {
    switch (type) {
      case 0: return 'Devamlı Üye';
      case 1: return 'Devamdan Muaf Üye';
      case 2: return 'Aidattan Muaf Üye';
      case 3: return 'Onursal Üye';
      case 4: return 'İzinli Üye';
      default: return 'Bilinmiyor';
    }
  };

  const getMembershipTypeColor = (type) => {
    switch (type) {
      case 0: return { bg: '#E7EDF4', color: '#0E4D94' }; // Regular - mavi
      case 1: return { bg: '#FFF8E1', color: '#FFA500' }; // ExcusedAttendance - turuncu
      case 2: return { bg: '#DFFFEA', color: '#17C653' }; // ExcusedDues - yeşil
      case 3: return { bg: '#F3E5F5', color: '#9C27B0' }; // Honorary - mor
      case 4: return { bg: '#E0F2F1', color: '#00796B' }; // Permitted - teal
      default: return { bg: '#e6e6e6', color: '#666666' };
    }
  };

  // Dashboard vb. yerlerden "Güncelleme Talepleri" sekmesine yönlendirme desteği
  useEffect(() => {
    const state = location.state;
    if (state?.openUpdateTab) {
      setTab(1);
      if (state.initialUpdateStatus !== undefined && state.initialUpdateStatus !== null) {
        setUpdateRequestsStatusFilter(String(state.initialUpdateStatus));
      } else {
        setUpdateRequestsStatusFilter('0');
      }
    }
  }, [location.state]);

  const handleViewDetail = async (request) => {
    setSelectedRequest(request);
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const detail = await fetchUpdateRequestById(request.id);
      setRequestDetail(detail);
    } catch (e) {
      setRequestDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      await approveUpdateRequest(requestId);
      // Refresh list
      const params = { pageNumber: updateRequestsPage, pageSize: updateRequestsPageSize };
      if (updateRequestsStatusFilter !== '') {
        params.status = Number(updateRequestsStatusFilter);
      }
      const { data, meta } = await fetchAllUpdateRequests(params);
      setUpdateRequests(data || []);
      setUpdateRequestsTotalPages(meta?.totalPages || 1);
      setDetailDialogOpen(false);
    } catch (e) {
      // Error handling
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;
    setProcessing(true);
    try {
      await rejectUpdateRequest(selectedRequest.id, rejectionReason);
      setRejectDialogOpen(false);
      setRejectionReason('');
      // Refresh list
      const params = { pageNumber: updateRequestsPage, pageSize: updateRequestsPageSize };
      if (updateRequestsStatusFilter !== '') {
        params.status = Number(updateRequestsStatusFilter);
      }
      const { data, meta } = await fetchAllUpdateRequests(params);
      setUpdateRequests(data || []);
      setUpdateRequestsTotalPages(meta?.totalPages || 1);
      setDetailDialogOpen(false);
    } catch (e) {
      // Error handling
    } finally {
      setProcessing(false);
    }
  };

  const formatBirthDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR');
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Üyeler</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: { xs: '100%', sm: 420 } }}>
          {tab === 0 && (
            <>
              <TextField
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
                placeholder="Üye ara..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (page !== 1) setPage(1);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {loading && searchActive ? <CircularProgress size={16} /> : null}
                        {searchQuery ? (
                          <IconButton
                            size="small"
                            aria-label="Aramayı temizle"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setSearchQuery('');
                              setPage(1);
                            }}
                            sx={{ color: '#a3a3a3' }}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ width: 16 }} />}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E7EDF4',
                  boxShadow: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  color: '#0E4D94',
                  '&:hover': {
                    backgroundColor: '#CFDBEA',
                    boxShadow: 'none',
                  },
                }}
                onClick={() => setCreateOpen(true)}
              >
                Yeni Üye
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            '&.Mui-selected': {
              color: '#0E4D94',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0E4D94',
          },
        }}
      >
        <Tab label="Üyeler" />
        <Tab label="Güncelleme Talepleri" />
      </Tabs>
      {tab === 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
              Durum Filtresi
            </Typography>
            <TextField
              select
              value={updateRequestsStatusFilter}
              onChange={(e) => {
                setUpdateRequestsStatusFilter(e.target.value);
                setUpdateRequestsPage(1);
              }}
              sx={{
                width: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '5px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0E4D94',
                  },
                },
                '& .MuiInputBase-input': {
                  py: .7,
                  px: 0.75,
                  fontSize: '14px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e6e6e6',
                },
              }}
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value={0}>Beklemede</MenuItem>
              <MenuItem value={1}>Onaylandı</MenuItem>
              <MenuItem value={2}>Reddedildi</MenuItem>
            </TextField>
          </Box>
        </Box>
      )}
      {tab === 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
              border: '1px solid #f5f5f5',
              py: 1,
              px: 2.5
            }}
          >
            <Table size="small">
                  <TableHead sx={{ bgcolor: 'transparent' }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Üyelik Tipi</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Yakını</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>E-posta Adresi</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Telefon Numarası</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>TC Kimlik No</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Doğum Tarihi</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}></TableCell>
                    </TableRow>
                  </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: pageSize }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === pageSize - 1 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Skeleton width={40} height={40} borderRadius="50%" />
                          <Skeleton width={150} height={20} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Skeleton width={120} height={24} borderRadius={1} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={200} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={120} height={20} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton width={32} height={32} borderRadius={6} />
                      </TableCell>
                    </TableRow>
                  ))
                    ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: '#a3a3a3' }}>
                        {searchActive ? 'Arama sonucu bulunamadı.' : 'Henüz üye eklenmemiş'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  ) : rows.map((m, index) => {
                  const membershipTypeColors = getMembershipTypeColor(m.membershipType ?? 0);
                  return (
                    <TableRow
                    key={m.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      '& .MuiTableCell-root': {
                        borderBottom: index === rows.length - 1 ? 'none' : '1px dashed #e8e8e8',
                        px: 1.5,
                        py: 1.5,
                        color: '#444444',
                        fontWeight: 500,
                      },
                    }}
                    onClick={() => navigate(`/members/${m.id}`)}
                    >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={resolveGroupsAssetUrl(m.profilePicture)} sx={{ width: 36, height: 36 }} />
                        <Typography variant="body2">{m.fullName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getMembershipTypeLabel(m.membershipType ?? 0)}
                        size="small"
                        sx={{
                          backgroundColor: membershipTypeColors.bg,
                          color: membershipTypeColors.color,
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {m.subMemberFullName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={resolveGroupsAssetUrl(m.subMemberProfilePicture)} sx={{ width: 36, height: 36 }} />
                          <Typography variant="body2">{m.subMemberFullName}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>{m.phoneNumber}</TableCell>
                    <TableCell>{m.tcKimlikNo || '-'}</TableCell>
                    <TableCell>{formatBirthDate(m.birthDate)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => { setSelected(m); setConfirmOpen(true); }}
                          sx={{
                            backgroundColor: '#FFEEF3',
                            borderRadius: '6px',
                            width: 32,
                            height: 32,
                            '&:hover': {
                              backgroundColor: '#FFD6E0',
                            },
                            '& svg': {
                              fontSize: '16px',
                              color: '#F8285A',
                            },
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <CreateMemberDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            onCreated={async () => {
              setLoading(true);
              try {
              const { data, meta } = await fetchAllMembers({ pageNumber: page, pageSize });
              setRows(data || []);
              setTotalPages(meta?.totalPages || 1);
              } catch (e) {
                setRows([]);
              } finally {
                setLoading(false);
              }
            }}
          />
          {!searchActive && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
            </Box>
          )}
        </>
      )}
      {tab === 1 && (
        <>
          {updateRequestsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f5f5f5',
                  py: 1,
                  px: 2.5
                }}
              >
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'transparent' }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Talep Eden</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Hedef Kişi</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Tip</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Talep Tarihi</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {updateRequests.map((req, index) => (
                      <TableRow
                        key={req.id}
                        hover
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: index === updateRequests.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            px: 1.5,
                            py: 1.5,
                            color: '#444444',
                            fontWeight: 500,
                          },
                        }}
                      >
                        <TableCell>{req.requestedByFullName}</TableCell>
                        <TableCell>{req.targetPersonFullName}</TableCell>
                        <TableCell>
                          <Chip
                            label={req.targetPersonType === 0 ? 'Üye' : 'Yakın'}
                            size="small"
                            sx={{
                              backgroundColor: req.targetPersonType === 0 ? '#E7EDF4' : '#F3E5F5',
                              color: req.targetPersonType === 0 ? '#0E4D94' : '#9C27B0',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(req.status)}
                            size="small"
                            sx={{
                              backgroundColor: req.status === 0 ? '#FFF8E1' : req.status === 1 ? '#DFFFEA' : '#FFEEF3',
                              color: req.status === 0 ? '#FFA500' : req.status === 1 ? '#17C653' : '#F8285A',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </TableCell>
                        <TableCell>{new Date(req.requestedAt).toLocaleString('tr-TR')}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Detay">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(req)}
                                sx={{
                                  backgroundColor: '#E7EDF4',
                                  borderRadius: '6px',
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: '#CFDBEA',
                                  },
                                  '& svg': {
                                    fontSize: '16px',
                                    color: '#0E4D94',
                                  },
                                }}
                              >
                                <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {req.status === 0 && (
                            <>
                              <Tooltip title="Onayla">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleApprove(req.id)}
                                    disabled={processing}
                                    sx={{
                                      backgroundColor: '#DFFFEA',
                                      borderRadius: '6px',
                                      width: 32,
                                      height: 32,
                                      '&:hover': {
                                        backgroundColor: '#C5F5D9',
                                      },
                                      '& svg': {
                                        fontSize: '16px',
                                        color: '#17C653',
                                      },
                                    }}
                                  >
                                    <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reddet">
                                  <IconButton
                                    size="small"
                                    onClick={() => { setSelectedRequest(req); setRejectDialogOpen(true); }}
                                    disabled={processing}
                                    sx={{
                                      backgroundColor: '#FFEEF3',
                                      borderRadius: '6px',
                                      width: 32,
                                      height: 32,
                                      '&:hover': {
                                        backgroundColor: '#FFD6E0',
                                      },
                                      '& svg': {
                                        fontSize: '16px',
                                        color: '#F8285A',
                                      },
                                    }}
                                  >
                                    <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination count={updateRequestsTotalPages} page={updateRequestsPage} onChange={(e, v) => setUpdateRequestsPage(v)} />
              </Box>
            </>
          )}
        </>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Üyeyi Sil
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {selected ? `${selected.fullName} üyesini ve bağlı yakınlarını silmek istediğinize emin misiniz?` : 'Seçili üyeyi silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
            if (!selected) return;
            try {
              await deleteMemberAction(selected.id);
              setConfirmOpen(false);
              setSelected(null);
              const { data, meta } = await fetchAllMembers({ pageNumber: page, pageSize });
              setRows(data || []);
              setTotalPages(meta?.totalPages || 1);
              } catch (e) { }
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#d91e47',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => { setDetailDialogOpen(false); setRequestDetail(null); }} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Güncelleme Talebi Detayı
          <IconButton
            onClick={() => { setDetailDialogOpen(false); setRequestDetail(null); }}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : requestDetail ? (
            <Stack spacing={3}>
              {/* Bilgiler Bölümü */}
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #e7e7e7',
                }}
              >
                <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.75, fontWeight: 500 }}>Talep Eden</Typography>
                      <Typography variant="body1" sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{requestDetail.requestedByFullName}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.75, fontWeight: 500 }}>Hedef Kişi</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body1" sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>{requestDetail.targetPersonFullName}</Typography>
                        <Chip
                          label={requestDetail.targetPersonType === 0 ? 'Üye' : 'Yakın'}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '11px',
                            fontWeight: 500,
                            backgroundColor: requestDetail.targetPersonType === 0 ? '#E7EDF4' : '#FFF4E6',
                            color: requestDetail.targetPersonType === 0 ? '#0E4D94' : '#FF9800',
                            border: 'none',
                          }}
                        />
                      </Stack>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.75, fontWeight: 500 }}>Durum</Typography>
                      <Chip
                        label={getStatusLabel(requestDetail.status)}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          backgroundColor: requestDetail.status === 0 ? '#FFF4E6' : requestDetail.status === 1 ? '#DFFFEA' : '#FFEEF3',
                          color: requestDetail.status === 0 ? '#FF9800' : requestDetail.status === 1 ? '#17C653' : '#F8285A',
                          border: 'none',
                        }}
                      />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.75, fontWeight: 500 }}>Talep Tarihi</Typography>
                      <Typography variant="body2" sx={{ fontSize: '14px', color: '#444444' }}>{new Date(requestDetail.requestedAt).toLocaleString('tr-TR')}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                {requestDetail.rejectionReason && (
                  <Box sx={{ mt: 2.5, pt: 2.5, borderTop: '1px solid #e6e6e6' }}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.75, fontWeight: 500 }}>Red Nedeni</Typography>
                    <Typography variant="body2" sx={{ fontSize: '14px', color: '#F8285A' }}>{requestDetail.rejectionReason}</Typography>
                  </Box>
                )}
              </Box>

              {/* Değişiklikler Bölümü */}
              {(() => {
                const changes = getMemberUpdateRequestChanges(
                  requestDetail.oldData,
                  requestDetail.newData,
                );
                if (changes.length === 0) return null;
                return (
                <>
                  <Divider sx={{ borderColor: '#e6e6e6' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 2 }}>
                      Değişiklikler
                    </Typography>
                    <Stack spacing={2.5}>
                {changes.map(({ field, label, oldDisplay, newDisplay }) => (
                          <Box key={field}>
                            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 600, color: '#444444', mb: 1 }}>
                            {label}
                          </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                              <Typography variant="body2" sx={{ fontSize: '14px', textDecoration: 'line-through', color: '#666666', opacity: 0.7 }}>
                                {oldDisplay}
                              </Typography>
                              <ArrowForwardIcon sx={{ fontSize: '16px', color: '#a3a3a3' }} />
                              <Chip
                                label={newDisplay}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  backgroundColor: '#FFF4E6',
                                  color: '#FF9800',
                                  border: 'none',
                                }}
                              />
                            </Stack>
                          </Box>
                ))}
                    </Stack>
                  </Box>
                </>
                );
              })()}
            </Stack>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '14px', color: '#F8285A' }}>Detay yüklenemedi.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          {requestDetail?.status === 0 && (
            <>
              <Button
                onClick={() => { setSelectedRequest(requestDetail); setRejectDialogOpen(true); }}
                disabled={processing}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  color: '#F8285A',
                  '&:hover': {
                    backgroundColor: 'rgba(248, 40, 90, 0.08)',
                  },
                  '&:disabled': {
                    color: '#a3a3a3',
                  },
                }}
              >
                Reddet
              </Button>
              <Button
                onClick={() => handleApprove(requestDetail.id)}
                variant="contained"
                disabled={processing}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  backgroundColor: '#0E4D94',
                  borderRadius: '5px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#0a3d7a',
                  },
                  '&:disabled': {
                    backgroundColor: '#e6e6e6',
                    color: '#a3a3a3',
                  },
                }}
              >
                Onayla
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => { setRejectDialogOpen(false); setRejectionReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Güncelleme Talebini Reddet
          <IconButton
            onClick={() => { setRejectDialogOpen(false); setRejectionReason(''); }}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
              Red Nedeni
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
              placeholder="Red nedeni giriniz..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: '#e6e6e6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#0E4D94',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0E4D94',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => { setRejectDialogOpen(false); setRejectionReason(''); }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            disabled={!rejectionReason.trim() || processing}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            Reddet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembersList;


